import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface Campaign {
  id: number;
  name: string;
  templateText: string;
}

const MessagingPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<number | null>(null);
  const [overrideSent, setOverrideSent] = useState(false);
  const [limit, setLimit] = useState<number>(100);
  const [sending, setSending] = useState(false);
  const [directTo, setDirectTo] = useState('');
  const [directBody, setDirectBody] = useState('');
  const [sendingDirect, setSendingDirect] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

  useEffect(() => {
    fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API_BASE}/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setCampaigns([]); // Set empty array on error
    }
  };

  const sendCampaignMessages = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCampaign) return;

    setSending(true);
    try {
      const response = await axios.post(`${API_BASE}/messages/send-campaign`, {
        campaignId: selectedCampaign,
        overrideSent,
        limit
      });
      
      alert(`Attempted to send ${response.data.attempted} messages. Check the results for details.`);
      console.log('Send results:', response.data.results);
    } catch (error) {
      console.error('Error sending campaign messages:', error);
      alert('Error sending messages');
    } finally {
      setSending(false);
    }
  };

  const sendDirectMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!directTo || !directBody) return;

    setSendingDirect(true);
    try {
      const response = await axios.post(`${API_BASE}/messages/send-direct`, {
        to: directTo,
        body: directBody
      });
      
      alert(`Message sent successfully! SID: ${response.data.sid}`);
      setDirectTo('');
      setDirectBody('');
    } catch (error) {
      console.error('Error sending direct message:', error);
      alert('Error sending message');
    } finally {
      setSendingDirect(false);
    }
  };

  const selectedCampaignData = campaigns.find(c => c.id === selectedCampaign);

  return (
    <div>
      <h1 className="page-title">Messaging</h1>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
        <div className="card">
          <h3>Send Campaign Messages</h3>
          <form onSubmit={sendCampaignMessages}>
            <div className="form-group">
              <label htmlFor="campaign">Select Campaign</label>
              <select
                id="campaign"
                value={selectedCampaign || ''}
                onChange={(e) => setSelectedCampaign(Number(e.target.value) || null)}
                required
              >
                <option value="">Select a campaign...</option>
                {campaigns.map(campaign => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              </select>
            </div>

            {selectedCampaignData && (
              <div style={{ marginBottom: '1rem' }}>
                <strong>Template Preview:</strong>
                <div style={{
                  background: '#f8f9fa',
                  padding: '1rem',
                  borderRadius: '4px',
                  marginTop: '0.5rem',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.875rem'
                }}>
                  {selectedCampaignData.templateText}
                </div>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="limit">Maximum Messages to Send</label>
              <input
                type="number"
                id="limit"
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                min="1"
                max="1000"
              />
              <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
                Limit the number of messages sent in this batch
              </small>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={overrideSent}
                  onChange={(e) => setOverrideSent(e.target.checked)}
                />
                Send to contacts who have already received messages
              </label>
              <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
                By default, contacts who have already received a message for this campaign will be skipped
              </small>
            </div>

            <button type="submit" className="btn" disabled={sending || !selectedCampaign}>
              {sending ? 'Sending...' : 'Send Campaign Messages'}
            </button>
          </form>
        </div>

        <div className="card">
          <h3>Send Direct Message</h3>
          <form onSubmit={sendDirectMessage}>
            <div className="form-group">
              <label htmlFor="directTo">Phone Number</label>
              <input
                type="tel"
                id="directTo"
                value={directTo}
                onChange={(e) => setDirectTo(e.target.value)}
                placeholder="+1234567890"
                required
              />
              <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
                Include country code (e.g., +1 for US)
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="directBody">Message</label>
              <textarea
                id="directBody"
                value={directBody}
                onChange={(e) => setDirectBody(e.target.value)}
                placeholder="Your message text here..."
                required
                style={{ minHeight: '120px' }}
              />
              <div style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
                Character count: {directBody.length}
                {directBody.length > 160 && (
                  <span style={{ color: '#e74c3c' }}> (Multiple SMS segments)</span>
                )}
              </div>
            </div>

            <button type="submit" className="btn" disabled={sendingDirect}>
              {sendingDirect ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </div>

      <div className="card" style={{ marginTop: '2rem' }}>
        <h3>Important Notes</h3>
        <ul style={{ margin: '1rem 0', paddingLeft: '2rem' }}>
          <li><strong>Deduplication:</strong> By default, contacts who have already received a message for a campaign will be skipped</li>
          <li><strong>Rate Limits:</strong> Twilio has rate limits - large campaigns may need to be sent in batches</li>
          <li><strong>Message Costs:</strong> Each SMS incurs a cost - be mindful when sending to large lists</li>
          <li><strong>Compliance:</strong> Ensure you have consent to send messages to all recipients</li>
          <li><strong>Testing:</strong> Test your templates with small batches before sending to large lists</li>
        </ul>
      </div>
    </div>
  );
};

export default MessagingPage;
